import * as React from 'react';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDBackChevron from '../../../../../../BaseComponents/Buttons/SpecificButtons/BackChevron/EDBackChevron';
import EDForwardChevron from '../../../../../../BaseComponents/Buttons/SpecificButtons/ForwardChevron/EDForwardChevron';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDStandardSelect from '../../../../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';
import ListFilteringComponent from '../../../../_Common/Components/ListFilteringComponent/ListFilteringComponent';
import { SchedulerFilterContext } from './SchedulerComponent/SchedulerFilterContext';
import {
  useIsExtraSmallScreen,
  useIsSmallScreen,
} from '../../../../../../Hooks/useFullscreenView';
import {
  getDate,
  getLabelForSpanningWeek,
  incDay,
  incMonth,
} from '../../../../../../Utils/dateUtils';
import { useContext } from 'react';
import EDSwitch from '../../../../../../BaseComponents/Switch/EDSwitch';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDDateCalender from '../../../../../../BaseComponents/DateCalender/EDDateCalender';
import EDPopover from '../../../../../../BaseComponents/Popover/EDPopover';
import { dateToStr, tryStrToDate } from '../../../../../../Utils/stringUtils';
import { findFormatter } from '../../../../../../Utils/Formatters/FindFormatter';
import { VirtuosoHandle } from 'react-virtuoso';
import { ViewDbSettingsContext } from './SchedulerComponent/ViewDbSettingsContext';

interface IProps {
  currentDate: Date;
  setCurrentDate: (newDate: Date) => void;
  currentView: string;
  setCurrentView: (newView: string) => void;
  views: string[];
  showOutlookBookings: boolean;
  setShowOutlookBookings: React.Dispatch<React.SetStateAction<boolean>>;
  anchorEl: HTMLElement | null;
  setOpenDateCalender: React.Dispatch<React.SetStateAction<boolean>>;
  openDateCalender: boolean;
  onDateCalenderClick?: (ref: EventTarget) => void;
  isSmallScreen: boolean;
  scrolledDate?: Date;
  listViewRef?: React.RefObject<VirtuosoHandle | null>;
}

const dateTimeGeneralFormatter = findFormatter('TEDFormatDateTimeGeneral');

export const KalenderHeader: React.FC<IProps> = props => {
  const schedulerFilterContext = useContext(SchedulerFilterContext);
  const viewDbSettingsContext = React.useContext(ViewDbSettingsContext);
  const isExtraSmallScreen = useIsExtraSmallScreen();
  const isSmallScreen = useIsSmallScreen();
  const all = props.views.find(view => view.toLowerCase() === 'all');
  const hasNewViews = props.views.length > 0;
  const WEEKDAYNAMES_THREE_LETTERS = [
    'sön',
    'mån',
    'tis',
    'ons',
    'tor',
    'fre',
    'lör',
  ];

  const onDateCalenderClick = (ref: EventTarget | null) => {
    if (ref && props.onDateCalenderClick) {
      props.onDateCalenderClick(ref);
    }
  };

  const getDateFormat = (currentView: string, isExtraSmallScreen: boolean) => {
    if (currentView === 'DayOverview') {
      return isExtraSmallScreen ? `Format="d mmm"` : `Format="d mmm yyyy"`;
    }
    if (currentView === 'WeekOverview' || currentView === 'WeekOverview2') {
      return isExtraSmallScreen ? `Format="mmm yyyy"` : `Format="mmmm yyyy"`;
    }
    return isExtraSmallScreen ? `Format="mmm yyyy"` : `Format="mmmm yyyy"`;
  };

  const getDateLabel = (currentDate: Date, currentView: string) => {
    const dateToUse =
      currentView === 'List' && props.scrolledDate
        ? props.scrolledDate
        : currentDate;
    if (
      currentView === 'Week2' ||
      currentView === 'WorkWeek' ||
      currentView === 'WeekOverview' ||
      currentView === 'WeekOverview2'
    ) {
      const overlappingLabel = getLabelForSpanningWeek(
        dateToUse,
        !isSmallScreen
      );
      if (overlappingLabel) {
        return overlappingLabel;
      }
    }

    const formattedDate = dateTimeGeneralFormatter.displayer.display(
      dateToStr(dateToUse),
      undefined,
      `${getDateFormat(currentView, isExtraSmallScreen)}`
    );

    if (currentView === 'DayOverview') {
      const dayName = WEEKDAYNAMES_THREE_LETTERS[dateToUse.getDay()];
      return `${dayName} ${formattedDate}`;
    }
    return formattedDate;
  };

  return (
    <EDBox
      sx={{
        display: 'flex',
        width: '100%',
        height: isSmallScreen ? '50px' : '68px',
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 10,
      }}
    >
      <EDButton
        label="Idag"
        margin={1.2}
        onClick={() => {
          const currentDate = getDate();
          viewDbSettingsContext.scrollToCurrentIndexRef.current?.();
          props.setCurrentDate(currentDate);
        }}
        outlined={!isSmallScreen ? true : false}
        padding={0}
      />
      {!props.isSmallScreen && (
        <EDBox
          sx={{
            display: props.currentView === 'List' ? 'none' : 'flex',
            width: 200,
          }}
        >
          <EDBackChevron
            noRightMargin
            onClick={() => {
              switch (props.currentView) {
                case 'Week':
                case 'Week2':
                case 'WorkWeek':
                case 'WeekOverview':
                case 'WeekOverview2':
                  props.setCurrentDate(incDay(props.currentDate, -7));
                  break;
                case 'Month':
                case 'Month2':
                  props.setCurrentDate(incMonth(props.currentDate, -1));
                  break;
                default:
                  props.setCurrentDate(incDay(props.currentDate, -1));
                  break;
              }
            }}
            color={'primary'}
          />
          <EDForwardChevron
            noLeftMargin
            onClick={() => {
              switch (props.currentView) {
                case 'Week':
                case 'Week2':
                case 'WorkWeek':
                case 'WeekOverview':
                case 'WeekOverview2':
                  props.setCurrentDate(incDay(props.currentDate, 7));
                  break;
                case 'Month':
                case 'Month2':
                  props.setCurrentDate(incMonth(props.currentDate, 1));
                  break;
                default:
                  props.setCurrentDate(incDay(props.currentDate, 1));
                  break;
              }
            }}
            color={'primary'}
          />
        </EDBox>
      )}
      <EDBox sx={{ display: 'flex', width: 220 }}>
        <EDButton
          label={getDateLabel(props.currentDate, props.currentView)}
          flat
          onClick={event => {
            props.setOpenDateCalender(!props.openDateCalender);
            onDateCalenderClick(event.target);
          }}
          icon={
            isSmallScreen ? (
              <EDIcon
                color="inherit"
                iconType={
                  props.openDateCalender ? 'arrow_drop_up' : 'arrow_drop_down'
                }
              />
            ) : undefined
          }
          alignIconToEnd
          testId="headerDateButton"
        />
        {props.anchorEl && (
          <EDPopover
            open={props.openDateCalender}
            anchorEl={props.anchorEl}
            onClose={() => {
              props.setOpenDateCalender(false);
            }}
          >
            <EDDateCalender
              onDateChange={value => {
                const d = tryStrToDate(value);

                if (d) {
                  viewDbSettingsContext.scrollToCurrentIndexRef.current?.();
                  props.setCurrentDate(d);
                }
                props.setOpenDateCalender(false);
              }}
              value={dateToStr(props.currentDate)}
            />
          </EDPopover>
        )}
      </EDBox>
      <EDBox
        sx={{
          display: 'flex',
          width: '100%',
          marginTop: isSmallScreen ? 0 : 1,
          marginLeft: isSmallScreen ? 0 : 4,
        }}
      >
        <ListFilteringComponent
          onSearchTextChanged={schedulerFilterContext.onSearchTextChanged}
          searchText={schedulerFilterContext.searchText ?? ''}
          sortedData={schedulerFilterContext.sortedData}
          filterAttributes={schedulerFilterContext.filterAttributes}
          listlength={schedulerFilterContext.listlength}
          sortedLength={schedulerFilterContext.sortedLength}
          visningsTyp={schedulerFilterContext.visningsTyp}
          filterAttributesPlacholder={`Filtrera på t.ex. ${schedulerFilterContext?.filterAttributes
            ?.filter(x => x.label !== '')
            .map(attr => attr.label)
            .join(', ')
            .replace(/, (?!.*, )/, ' eller ')}`}
          width={isExtraSmallScreen || isSmallScreen ? '100%' : 350}
        />
      </EDBox>
      {!isSmallScreen && (
        <EDBox
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <EDIcon iconType="outlook" />
          <EDSwitch
            title={
              props.showOutlookBookings
                ? 'Dölj Outlook-bokningar'
                : 'Visa Outlook-bokningar'
            }
            checked={props.showOutlookBookings}
            onChange={(_, checked) => {
              props.setShowOutlookBookings &&
                props.setShowOutlookBookings(checked);
            }}
          />
        </EDBox>
      )}
      {!(isSmallScreen && hasNewViews) && (
        <EDBox sx={{ display: 'flex', width: 180, marginRight: 2 }}>
          <EDStandardSelect
            value={props.currentView}
            items={[
              { header: 'Dag', value: 'Day' },
              { header: 'Dag', value: 'Day2' },
              { header: 'Vecka', value: 'Week' },
              { header: 'Vecka', value: 'Week2' },
              { header: 'Månad', value: 'Month' },
              { header: 'Månad', value: 'Month2' },
              { header: 'Arbetsvecka', value: 'WorkWeek' },
              { header: 'Översikt dag', value: 'DayOverview' },
              { header: 'Översikt vecka 1', value: 'WeekOverview' },
              { header: 'Översikt vecka 2', value: 'WeekOverview2' },
              { header: 'Lista', value: 'List' },
            ].filter(item => {
              if (hasNewViews) {
                return (
                  all ||
                  props.views.find(
                    view => view.toLowerCase() === item.value.toLowerCase()
                  )
                );
              } else {
                return ['Day', 'Week', 'Month'].includes(item.value);
              }
            })}
            onChange={newValue => {
              props.setCurrentView(newValue);
            }}
            testId="viewSelect"
          />
        </EDBox>
      )}
    </EDBox>
  );
};
